import type { Provider } from '@angular/core';
import { provideTranslocoScope } from '@jsverse/transloco';
import { i18nLoader as contactI18nLoader } from './contact/i18n-loader';
import { i18nLoader as countryI18nLoader } from './country/i18n-loader';
import { i18nLoader } from './i18n-loader';

/**
 * Provides transloco scopes for the Shared domain and its sub-domains.
 *
 * This provider aggregates multiple scope configurations for the shared library:
 * - Main Shared Scope: Contains core shared translations
 * - Contact Scope: Contains contact-related translations (shared-contact)
 * - Country Scope: Contains country-related translations (shared-country)
 *
 * Scope Details:
 * - Scope: 'shared' (used in templates: t['shared:key'])
 * - Scope: 'sharedContact' (used in templates: t['sharedContact:key'])
 * - Scope: 'sharedCountry' (used in templates: t['sharedCountry:key'])
 *
 * Usage in App Configuration:
 * The shared scope provider is registered at the application root in app.config.ts
 * to provide core shared domain translations globally.
 *
 * Translation Files:
 * - shared/i18n/en.json, ar.json - Shared translations
 * - shared/contact/i18n/en.json, ar.json - Contact translations
 * - shared/country/i18n/en.json, ar.json - Country translations
 *
 * @returns Angular environment providers for transloco scopes.
 * @see https://ngneat.github.io/transloco/docs/scope-resolution
 * @example Component usage (rarely needed, as shared is global)
 * ```ts
 * Component({
 *   providers: [provideTranslocoScopeSharedI18n()]
 * })
 * export class MyComponent {}
 * ```
 */
export function provideTranslocoScopeSharedI18n(): Provider {
  return provideTranslocoScope(
    { scope: 'shared', loader: i18nLoader },
    { scope: 'sharedContact', loader: contactI18nLoader },
    { scope: 'sharedCountry', loader: countryI18nLoader }
  );
}
